/**
 * Billing Page - Overview of all tenant subscriptions and billing
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Skeleton,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Sms as SmsIcon,
  Download as DownloadIcon,
  Description as ReportIcon,
} from '@mui/icons-material';
import { useAllSubscriptions, useBillingReport } from '../hooks';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import type { SubscriptionStatus, BillingCycle, BillingReport } from '../types';
import { useSnackbar } from 'notistack';

const statusColors: Record<SubscriptionStatus, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  TRIAL: 'warning',
  ACTIVE: 'success',
  PAST_DUE: 'error',
  CANCELLED: 'error',
  SUSPENDED: 'error',
};

const billingCycleLabels: Record<BillingCycle, string> = {
  MONTHLY: 'Månedlig',
  YEARLY: 'Årlig',
};

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

function StatsCard({ title, value, subtitle, icon, color }: StatsCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              color: color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

type TabValue = 'all' | 'active' | 'trial' | 'issues';

// Helper function to generate CSV from billing report
function generateCSV(report: BillingReport): string {
  const headers = [
    'Tenant',
    'Slug',
    'Status',
    'Faktureringsperiode',
    'Moduler',
    'Modulpris (mnd)',
    'Rabatt %',
    'Rabatt beløp',
    'Mnd. abonnementspris',
    'Fakturabeløp',
    'SMS inkludert',
    'SMS brukt',
    'SMS overskridelse',
    'SMS overskridelsespris',
    'SMS kostnad',
    'Total å fakturere',
  ];

  const rows = report.tenants.map((t) => [
    t.tenantName,
    t.tenantSlug,
    t.subscriptionStatus,
    t.billingCycle === 'YEARLY' ? 'Årlig' : 'Månedlig',
    t.modules.map((m) => m.name).join('; '),
    t.moduleSubtotal,
    t.discountPercent ?? 0,
    t.discountAmount,
    t.monthlySubscriptionPrice,
    t.billingAmount, // Actual amount to bill
    t.smsIncluded,
    t.smsUsed,
    t.smsOverage,
    t.smsOveragePrice,
    t.smsOverageCost,
    t.totalMonthlyBilling,
  ]);

  // Add summary row
  rows.push([]);
  rows.push([
    'TOTALT',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    report.summary.totalSubscriptionRevenue,
    '',
    '',
    '',
    '',
    report.summary.totalSmsOverageRevenue,
    report.summary.totalRevenue,
  ]);

  const csvContent = [
    headers.join(';'),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(';')),
  ].join('\n');

  return csvContent;
}

// Helper function to download file
function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob(['\ufeff' + content], { type: `${type};charset=utf-8` }); // BOM for Excel
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function BillingPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { data: subscriptions, isLoading } = useAllSubscriptions();
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState<TabValue>('all');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportPeriod, setExportPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Fetch billing report when export dialog is open
  const { data: billingReport, isLoading: isLoadingReport, error: reportError } = useBillingReport(exportPeriod);

  const handleExportCSV = useCallback(() => {
    if (!billingReport) return;

    try {
      const csv = generateCSV(billingReport);
      downloadFile(csv, `faktureringsrapport-${exportPeriod}.csv`, 'text/csv');
      enqueueSnackbar('Rapport eksportert', { variant: 'success' });
    } catch {
      enqueueSnackbar('Kunne ikke eksportere rapport', { variant: 'error' });
    }
  }, [billingReport, exportPeriod, enqueueSnackbar]);

  const handleExportJSON = useCallback(() => {
    if (!billingReport) return;

    try {
      const json = JSON.stringify(billingReport, null, 2);
      downloadFile(json, `faktureringsrapport-${exportPeriod}.json`, 'application/json');
      enqueueSnackbar('Rapport eksportert', { variant: 'success' });
    } catch {
      enqueueSnackbar('Kunne ikke eksportere rapport', { variant: 'error' });
    }
  }, [billingReport, exportPeriod, enqueueSnackbar]);

  // Calculate statistics (exclude internal tenants from billing)
  const stats = useMemo(() => {
    if (!subscriptions) return { total: 0, active: 0, trial: 0, issues: 0, mrr: 0, smsUsage: 0 };

    // Filter out internal tenants for billing purposes
    const billableSubscriptions = subscriptions.filter((s) => !s.isInternal);

    const active = billableSubscriptions.filter((s) => s.status === 'ACTIVE').length;
    const trial = billableSubscriptions.filter((s) => s.status === 'TRIAL').length;
    const issues = billableSubscriptions.filter((s) =>
      ['PAST_DUE', 'SUSPENDED', 'CANCELLED'].includes(s.status)
    ).length;

    // Calculate MRR based on actual module prices
    const mrr = billableSubscriptions
      .filter((s) => s.status === 'ACTIVE' || s.status === 'TRIAL')
      .reduce((sum, s) => {
        if (s.customPrice) return sum + s.customPrice;
        // Sum up actual module prices (priceOverride if set, otherwise basePrice)
        const moduleTotal = (s.modules || [])
          .filter((m) => m.status === 'ACTIVE')
          .reduce((modSum, m) => {
            const price = m.priceOverride ?? m.module?.basePrice ?? 0;
            return modSum + price;
          }, 0);
        // Apply discount if active
        if (s.discountPercent && s.discountEndsAt && new Date(s.discountEndsAt) > new Date()) {
          return sum + Math.round(moduleTotal * (1 - s.discountPercent / 100));
        }
        return sum + moduleTotal;
      }, 0);

    return {
      total: billableSubscriptions.length,
      active,
      trial,
      issues,
      mrr,
      smsUsage: billableSubscriptions.reduce((sum, s) => sum + s.smsIncluded, 0),
    };
  }, [subscriptions]);

  // Filter subscriptions based on tab and search (exclude internal tenants from billing view)
  const filteredSubscriptions = useMemo(() => {
    if (!subscriptions) return [];

    // Filter out internal tenants - they don't get billed
    let filtered = subscriptions.filter((s) => !s.isInternal);

    // Filter by tab
    switch (tabValue) {
      case 'active':
        filtered = filtered.filter((s) => s.status === 'ACTIVE');
        break;
      case 'trial':
        filtered = filtered.filter((s) => s.status === 'TRIAL');
        break;
      case 'issues':
        filtered = filtered.filter((s) =>
          ['PAST_DUE', 'SUSPENDED', 'CANCELLED'].includes(s.status)
        );
        break;
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.tenant?.name.toLowerCase().includes(searchLower) ||
          s.tenant?.slug.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [subscriptions, tabValue, search]);

  // Calculate trial progress
  const getTrialProgress = (trialEndsAt: string | null, startedAt: string) => {
    if (!trialEndsAt) return null;
    const start = new Date(startedAt).getTime();
    const end = new Date(trialEndsAt).getTime();
    const now = Date.now();
    const total = end - start;
    const elapsed = now - start;
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  // Get days remaining in trial
  const getTrialDaysRemaining = (trialEndsAt: string | null) => {
    if (!trialEndsAt) return null;
    const end = new Date(trialEndsAt).getTime();
    const now = Date.now();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Billing & Subscriptions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Oversikt over alle abonnementer og fakturering
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<ReportIcon />}
          onClick={() => setExportDialogOpen(true)}
        >
          Generer Rapport
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Totalt Abonnementer"
            value={stats.total}
            icon={<PeopleIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Aktive"
            value={stats.active}
            subtitle={`${stats.trial} i prøveperiode`}
            icon={<CheckCircleIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Problemer"
            value={stats.issues}
            subtitle="Forfalt, suspendert, kansellert"
            icon={<WarningIcon />}
            color={stats.issues > 0 ? '#d32f2f' : '#757575'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Est. MRR"
            value={`${stats.mrr.toLocaleString()} kr`}
            subtitle="Månedlig gjentakende inntekt"
            icon={<MoneyIcon />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Subscriptions Table */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{ px: 2 }}
          >
            <Tab label={`Alle (${stats.total})`} value="all" />
            <Tab label={`Aktive (${stats.active})`} value="active" />
            <Tab label={`Prøveperiode (${stats.trial})`} value="trial" />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Problemer ({stats.issues})
                  {stats.issues > 0 && (
                    <WarningIcon fontSize="small" color="error" />
                  )}
                </Box>
              }
              value="issues"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            placeholder="Søk etter tenant..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Fakturering</TableCell>
                <TableCell>Moduler</TableCell>
                <TableCell>SMS Kvote</TableCell>
                <TableCell>Pris</TableCell>
                <TableCell>Neste Faktura</TableCell>
                <TableCell align="right">Handlinger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredSubscriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      Ingen abonnementer funnet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubscriptions.map((subscription) => {
                  const trialDays = getTrialDaysRemaining(subscription.trialEndsAt);
                  const trialProgress = getTrialProgress(subscription.trialEndsAt, subscription.startedAt);

                  return (
                    <TableRow
                      key={subscription.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/tenants/${subscription.tenantId}`)}
                    >
                      <TableCell>
                        <Box>
                          <Typography fontWeight={500}>
                            {subscription.tenant?.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {subscription.tenant?.slug}
                          </Typography>
                          {subscription.isInternal && (
                            <Chip
                              label="Intern"
                              size="small"
                              color="info"
                              sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            label={subscription.status}
                            size="small"
                            color={statusColors[subscription.status]}
                          />
                          {subscription.status === 'TRIAL' && trialDays !== null && (
                            <Box sx={{ mt: 1, minWidth: 100 }}>
                              <Typography variant="caption" color="text.secondary">
                                {trialDays} dager igjen
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={trialProgress || 0}
                                color={trialDays <= 3 ? 'error' : trialDays <= 7 ? 'warning' : 'primary'}
                                sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                              />
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {billingCycleLabels[subscription.billingCycle]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {subscription.modules?.length || 0} moduler
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <SmsIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {subscription.smsIncluded.toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {subscription.customPrice !== null ? (
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {subscription.customPrice.toLocaleString()} kr
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Tilpasset pris
                            </Typography>
                          </Box>
                        ) : subscription.discountPercent ? (
                          <Box>
                            <Typography variant="body2">
                              -{subscription.discountPercent}% rabatt
                            </Typography>
                          </Box>
                        ) : subscription.modules?.some((m) => m.priceOverride !== null) ? (
                          <Box>
                            <Typography variant="body2" fontWeight={500} color="warning.main">
                              Modulpriser
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {subscription.modules.filter((m) => m.priceOverride !== null).length} tilpasset
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Standardpris
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {subscription.nextBillingDate ? (
                          format(new Date(subscription.nextBillingDate), 'dd.MM.yyyy')
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Se detaljer">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/tenants/${subscription.tenantId}`);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Notes Section */}
      {filteredSubscriptions.some((s) => s.notes) && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Notater
            </Typography>
            {filteredSubscriptions
              .filter((s) => s.notes)
              .map((s) => (
                <Box
                  key={s.id}
                  sx={{
                    p: 2,
                    mb: 1,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    {s.tenant?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.notes}
                  </Typography>
                </Box>
              ))}
          </CardContent>
        </Card>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportIcon color="primary" />
            Generer Faktureringsrapport
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="Periode"
              type="month"
              value={exportPeriod}
              onChange={(e) => setExportPeriod(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Velg måned for faktureringsrapport"
              fullWidth
            />

            {isLoadingReport && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Genererer rapport...
                </Typography>
              </Box>
            )}

            {reportError && (
              <Alert severity="error">
                Kunne ikke laste rapport. Prøv igjen.
              </Alert>
            )}

            {billingReport && !isLoadingReport && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Rapport for {format(new Date(billingReport.period + '-01'), 'MMMM yyyy', { locale: nb })}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Fakturerbare kunder</Typography>
                      <Typography variant="h6">{billingReport.summary.totalTenants}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Abonnementsinntekter</Typography>
                      <Typography variant="h6">{billingReport.summary.totalSubscriptionRevenue.toLocaleString()} kr</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">SMS Overskridelse</Typography>
                      <Typography variant="h6">{billingReport.summary.totalSmsOverageRevenue.toLocaleString()} kr</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Total Inntekt</Typography>
                      <Typography variant="h6" color="primary.main" fontWeight={700}>
                        {billingReport.summary.totalRevenue.toLocaleString()} kr
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Info about skipped tenants */}
                  {(billingReport.summary.skippedTrials > 0 ||
                    billingReport.summary.skippedInternal > 0 ||
                    billingReport.summary.skippedYearlyNotDue > 0) && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Ekskludert fra rapporten:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {billingReport.summary.skippedTrials > 0 && (
                          <Chip
                            size="small"
                            label={`${billingReport.summary.skippedTrials} prøveperiode`}
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {billingReport.summary.skippedInternal > 0 && (
                          <Chip
                            size="small"
                            label={`${billingReport.summary.skippedInternal} interne`}
                            color="info"
                            variant="outlined"
                          />
                        )}
                        {billingReport.summary.skippedYearlyNotDue > 0 && (
                          <Chip
                            size="small"
                            label={`${billingReport.summary.skippedYearlyNotDue} årlige (ikke forfalt)`}
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportDialogOpen(false)}>
            Lukk
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportJSON}
            disabled={!billingReport || isLoadingReport}
          >
            Last ned JSON
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={!billingReport || isLoadingReport}
          >
            Last ned CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
